import { z } from 'zod'

/**
 * Schema for creating or editing a category.
 *
 * `parentId` null means "no parent" — a main group. It never means "unchanged": removing a parent
 * has to be expressible, and one field cannot carry both meanings.
 */
export const buildCategorySchema = (t: (key: string) => string) =>
  z.object({
    code: z
      .string()
      .min(1, t('validation.codeRequired'))
      .max(64, t('validation.codeMax'))
      .regex(/^[a-z0-9-_]+$/, t('validation.codePattern')),
    name: z
      .string()
      .min(1, t('validation.nameRequired'))
      .max(200, t('validation.nameMax')),
    parentId: z.number().int().nullable(),
    sortOrder: z.number().int(t('validation.sortOrderInt')),
    active: z.boolean(),
  })

/** @deprecated Kept for callers that only create; use {@link buildCategorySchema}. */
export const buildCreateCategorySchema = buildCategorySchema

export type CategoryFormData = z.infer<ReturnType<typeof buildCategorySchema>>

export type CreateCategoryFormData = CategoryFormData

/**
 * The reasons the server refuses a parent change, each needing a different next step from the user
 * — which is why they are distinguishable rather than one generic "invalid".
 */
export const CATEGORY_ERROR_CODES = [
  'parent_is_self',
  'parent_not_found',
  'parent_is_subgroup',
  'category_has_children',
  'category_has_active_children',
  'collector_cannot_be_nested',
  'collector_cannot_be_parent',
  'category_not_found',
  'category_inactive',
  'duplicate_code',
] as const

export type CategoryErrorCode = (typeof CATEGORY_ERROR_CODES)[number]

/**
 * Maps a server reason code to a message key, falling back to a readable generic message rather
 * than to a blank dialog when a code we do not know about arrives.
 */
export function categoryErrorKey(code: string | undefined): string {
  if (code && (CATEGORY_ERROR_CODES as ReadonlyArray<string>).includes(code)) {
    return `errors.${code}`
  }
  return 'errors.unknown'
}
